import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, MoreHorizontal, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AxiosError } from 'axios';
import api from '@/services/api';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/FormDialog';
import { FormField } from '@/components/forms/FormField';
import { FormSelect } from '@/components/forms/FormSelect';
import { formatDate, getInitials } from '@/lib/formatters';
import { useAuthStore } from '@/stores/authStore';
import type { User, PaginatedResponse } from '@/types/table';
import { useState } from 'react';

const ROLES = ['ADMIN', 'MANAGER', 'USER'] as const;
type Role = (typeof ROLES)[number];

const ROLE_OPTIONS = ROLES.map((role) => ({ label: role, value: role }));

const createUserSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Enter a valid email address'),
  password: z
    .string()
    .min(8, 'At least 8 characters')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Needs an uppercase letter, a lowercase letter and a number'),
  role: z.enum(ROLES),
});
type CreateUserFormData = z.infer<typeof createUserSchema>;

/**
 * The server refuses changes that would lock the organisation out of its own
 * tool — demoting the last administrator, deactivating yourself, and so on. It
 * explains why in the response, so show that rather than a generic failure.
 */
function serverMessage(error: unknown, fallback: string): string {
  const message = (error as AxiosError<{ message?: string | string[] }>)?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
}

export function TeamPage() {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((state) => state.user?.id);

  const { data, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async (): Promise<PaginatedResponse<User>> => {
      const res = await api.get('/users');
      return res.data;
    },
  });
  const users = data?.data ?? [];

  const [deactivateUser, setDeactivateUser] = useState<User | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { firstName: '', lastName: '', email: '', password: '', role: 'USER' },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['users'] });

  const createUser = useMutation({
    mutationFn: async (values: CreateUserFormData) => {
      await api.post('/users', values);
    },
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
      form.reset();
      toast.success('User created');
    },
    onError: (error) => toast.error(serverMessage(error, 'Failed to create user')),
  });

  const toggleActive = useMutation({
    mutationFn: async (user: User) => {
      await api.patch(`/users/${user.id}`, { isActive: !user.isActive });
    },
    onSuccess: () => {
      invalidate();
      toast.success('User status updated');
    },
    onError: (error) => toast.error(serverMessage(error, 'Failed to update user status')),
  });

  const changeRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: Role }) => {
      await api.patch(`/users/${userId}`, { role });
    },
    onSuccess: () => {
      invalidate();
      toast.success('User role updated');
    },
    onError: (error) => toast.error(serverMessage(error, 'Failed to update role')),
  });

  // An administrator cannot change their own role or deactivate themselves —
  // the server rejects both. Reflect that here rather than offering an action
  // that always fails.
  const activeAdmins = users.filter((u) => u.role === 'ADMIN' && u.isActive);

  if (isLoading) return <LoadingSpinner />;

  return (
    <PageContainer
      title="Team Management"
      description="Manage users and their roles"
      actions={
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Team Members ({users.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const isSelf = user.id === currentUserId;
                const isLastAdmin =
                  user.role === 'ADMIN' && user.isActive && activeAdmins.length === 1;

                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatarUrl} />
                          <AvatarFallback className="text-xs">
                            {getInitials(user.firstName, user.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">
                          {user.firstName} {user.lastName}
                        </span>
                        {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge
                          variant={
                            user.role === 'ADMIN'
                              ? 'default'
                              : user.role === 'MANAGER'
                                ? 'secondary'
                                : 'outline'
                          }
                        >
                          {user.role}
                        </Badge>
                        {isLastAdmin && (
                          <span
                            title="The only active administrator — this account cannot be demoted, deactivated or deleted."
                            className="text-muted-foreground"
                          >
                            <ShieldCheck className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.isActive ? 'success' : 'destructive'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(user.lastLoginAt)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                            {isSelf ? 'You cannot change your own role' : 'Set role'}
                          </DropdownMenuLabel>
                          {ROLES.map((role) => (
                            <DropdownMenuItem
                              key={role}
                              disabled={
                                isSelf || user.role === role || (isLastAdmin && role !== 'ADMIN')
                              }
                              onClick={() => changeRole.mutate({ userId: user.id, role })}
                            >
                              {role}
                              {user.role === role && (
                                <span className="ml-auto text-xs text-muted-foreground">current</span>
                              )}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            disabled={user.isActive && (isSelf || isLastAdmin)}
                            onClick={() => setDeactivateUser(user)}
                          >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <FormDialog
        open={addOpen}
        onOpenChange={(open) => {
          setAddOpen(open);
          if (!open) form.reset();
        }}
        title="Add User"
        description="Create an account for a team member. They sign in with the password you set here."
        submitLabel="Create User"
        isSubmitting={createUser.isPending}
        onSubmit={form.handleSubmit((values) => createUser.mutate(values))}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField form={form} name="firstName" label="First Name" required />
            <FormField form={form} name="lastName" label="Last Name" required />
          </div>
          <FormField form={form} name="email" label="Email" type="email" required />
          <FormField
            form={form}
            name="password"
            label="Temporary Password"
            type="password"
            description="At least 8 characters, with an uppercase letter, a lowercase letter and a number."
            required
          />
          <FormSelect form={form} name="role" label="Role" options={ROLE_OPTIONS} required />
        </div>
      </FormDialog>

      <ConfirmDialog
        open={!!deactivateUser}
        onOpenChange={() => setDeactivateUser(null)}
        title={deactivateUser?.isActive ? 'Deactivate User' : 'Activate User'}
        description={`Are you sure you want to ${deactivateUser?.isActive ? 'deactivate' : 'activate'} ${deactivateUser?.firstName} ${deactivateUser?.lastName}?`}
        variant={deactivateUser?.isActive ? 'destructive' : 'default'}
        confirmLabel={deactivateUser?.isActive ? 'Deactivate' : 'Activate'}
        onConfirm={() => {
          if (deactivateUser) {
            toggleActive.mutate(deactivateUser);
            setDeactivateUser(null);
          }
        }}
        isLoading={toggleActive.isPending}
      />
    </PageContainer>
  );
}
